import React, { useCallback, useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Award, CheckCircle2, Copy, Lock, MessageCircle, Printer } from "lucide-react";
import { Card, Empty, Field, Modal, Pill, btnCls, btnGhost, inputCls, waNumber } from "./ui";
import { Certificate, GradePill, ReportBody, STATUS, fmtWeek, printReport, reportMessage, studentPhone } from "./shared";

async function copyText(text) {
  try { await navigator.clipboard.writeText(text); toast.success("تم النسخ"); }
  catch { toast.error("تعذّر النسخ، انسخ النص يدويًا"); }
}

function ReportModal({ id, isAdmin, counselorName, onClose, onChanged }) {
  const [rep, setRep] = useState(null);
  const [note, setNote] = useState("");
  const [cert, setCert] = useState(false);
  const load = useCallback(() => api.get(`/followup/reports/${id}`).then((r) => { setRep(r.data); setNote(r.data.sup_note || ""); }).catch((e) => toast.error(formatErr(e))), [id]);
  useEffect(() => { load(); }, [load]);
  if (!rep) return null;
  const approve = async () => {
    try { await api.post(`/followup/reports/${id}/approve`, { sup_note: note }); toast.success("تم اعتماد التقرير"); onChanged(); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const sent = async () => { try { await api.post(`/followup/reports/${id}/sent`); onChanged(); load(); } catch (e) { toast.error(formatErr(e)); } };
  const [label, cls] = STATUS[rep.status] || STATUS.pending;
  if (rep.hidden) return (
    <Modal title="التقرير الأسبوعي" onClose={onClose}>
      <div className="text-center py-8 space-y-2"><Lock className="w-8 h-8 mx-auto text-[#8A6D2B]" /><div>يظهر محتوى التقرير بعد اعتماد المشرف الإرشادي.</div><Pill className={cls}>{label}</Pill></div>
    </Modal>
  );
  const msg = reportMessage(rep, counselorName);
  const phone = studentPhone(rep.student);
  const eligible = rep.eligibility?.thanks || rep.eligibility?.honor;
  return (
    <Modal title={`التقرير الأسبوعي — ${rep.student.name} — ${fmtWeek(rep.week)}`} onClose={onClose} wide>
      <div className="space-y-4">
        <div className="no-print flex flex-wrap items-center gap-2"><Pill className={cls}>{label}</Pill>{rep.reason && <span className="text-sm text-[#4A554E]">سبب المتابعة: {rep.reason}</span>}</div>
        {cert ? <Certificate rep={rep} /> : <div className="report-print"><ReportBody rep={rep} /></div>}
        {isAdmin && (
          <div className="no-print border-t border-[#e6e2d8] pt-3 space-y-2">
            <Field label="ملاحظة المشرف الإرشادي"><textarea className={inputCls} rows={2} maxLength={600} value={note} disabled={rep.status !== "pending"} onChange={(e) => setNote(e.target.value)} /></Field>
            {rep.status === "pending" && <button className={btnCls} onClick={approve} data-testid="fu-approve"><CheckCircle2 className="w-4 h-4" /> اعتماد التقرير</button>}
          </div>
        )}
        {!isAdmin && rep.sup_note && <div className="rounded-xl bg-[#F9F8F6] border border-[#e6e2d8] p-3 text-sm"><b>ملاحظة المشرف الإرشادي:</b> {rep.sup_note}</div>}
        <div className="no-print flex flex-wrap gap-2 border-t border-[#e6e2d8] pt-3">
          {!isAdmin && rep.status !== "pending" && (
            <>
              <a className={btnCls} target="_blank" rel="noopener noreferrer" href={`https://wa.me/${waNumber(phone)}?text=${encodeURIComponent(msg)}`} onClick={() => rep.status === "approved" && sent()} data-testid="fu-report-wa"><MessageCircle className="w-4 h-4" /> إرسال لولي الأمر عبر واتساب</a>
              <button className={btnGhost} onClick={() => { copyText(msg); if (rep.status === "approved") sent(); }}><Copy className="w-4 h-4" /> نسخ نص التقرير</button>
            </>
          )}
          {eligible && <button className={btnGhost} onClick={() => setCert(!cert)} data-testid="fu-cert-btn"><Award className="w-4 h-4" /> {cert ? "عرض التقرير" : rep.eligibility?.honor ? "شهادة التكريم" : "شهادة الشكر"}</button>}
          <button className={btnGhost} onClick={printReport}><Printer className="w-4 h-4" /> طباعة</button>
        </div>
      </div>
    </Modal>
  );
}

export default function ReportsList({ isAdmin, counselorName = "", refreshKey = 0 }) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(null);
  const load = useCallback(() => { api.get("/followup/reports", { params: status ? { status } : {} }).then((r) => setRows(r.data)).catch((e) => toast.error(formatErr(e))); }, [status]);
  useEffect(() => { load(); }, [load, refreshKey]);
  return (
    <Card title="التقارير الأسبوعية" actions={
      <select className={`${inputCls} !w-auto`} value={status} onChange={(e) => setStatus(e.target.value)}>
        <option value="">الحالة: الكل</option>
        {Object.entries(STATUS).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
      </select>}>
      {rows.length === 0 ? <Empty>لا توجد تقارير</Empty> : (
        <div className="divide-y divide-[#f0ede4]" data-testid="fu-reports">
          {rows.map((r) => {
            const [label, cls] = STATUS[r.status] || STATUS.pending;
            return (
              <button key={r.id} onClick={() => setOpen(r.id)} className="w-full text-start flex flex-wrap items-center justify-between gap-2 py-3 hover:bg-[#F9F8F6] px-2 rounded-lg">
                <div>
                  <div className="font-medium">{r.student?.name} <span className="text-xs text-[#4A554E]">{r.student?.grade}</span></div>
                  <div className="text-xs text-[#4A554E]">أسبوع {fmtWeek(r.week)}{isAdmin && r.counselor_name ? ` — المرشد: ${r.counselor_name}` : ""}{isAdmin && r.school_name ? ` — ${r.school_name}` : ""}</div>
                </div>
                <div className="flex items-center gap-2">
                  {(r.eligibility?.thanks || r.eligibility?.honor) && <Pill className="bg-[#B08D3C]/15 text-[#8A6D2B]">★</Pill>}
                  {r.pct != null && <GradePill pct={r.pct} />}
                  <Pill className={cls}>{label}</Pill>
                </div>
              </button>
            );
          })}
        </div>
      )}
      {open && <ReportModal id={open} isAdmin={isAdmin} counselorName={counselorName} onClose={() => setOpen(null)} onChanged={load} />}
    </Card>
  );
}
